import apiClient, { getSsoToken } from "./apiClient";
import { isOfflineMode } from "@/helpers/offlineMode";
import { quoteSnapshotService } from "./QuoteSnapshotService";
import {
  mockDashboardStatsPeriod,
  mockRequestsByTypePeriod,
  mockTeamPerformancePeriod,
  mockUnderwritingDashboard,
  mockQueueApplications,
  mockQueueDashboardStatsPeriod,
  mockQueueTeamPerformancePeriod,
  mockBaseApplicationDetails,
  mockPrepareApplication,
  mockQueueClaimHistory,
} from "./underwritingMockData";
import {
  UWDashboardStatsResponse,
  UWQueueDashboardStats,
  UWQueueAgentRow,
  UWQueueActionPayload,
  RequestTypeRow,
  AgentPerformanceRow,
  UnderwritingActivity,
  RequestStatus,
  ActionDecision,
  NoteModel,
  NoteListResponse,
  AddNoteRequestModel,
  TranscriptEntry,
  CallSegment,
  TranscriptSegment,
  MessageEntry,
  MessageChannel,
  TwilioMessage,
  TwilioHistoryResponse,
  BaseApplicationDetails,
  PrepareApplicationResponse,
  PrepareApplicationProposal,
  AdditionalLenderQuestions,
  UnderwritingQueueClaimHistory,
} from "@/models/underwriting";
import {
  parseAdditionalLenderQuestions,
  serializeAdditionalLenderQuestions,
} from "@/helpers/additionalLenderQuestions";
import { FinanceCompany } from "@cf247/pricing/dist/enums";

// Types matching the raw API response shapes

export interface UWRequestPayload {
  applicationId: number;
  agentName: string;
  createdOn: string;
  bestLender: FinanceCompany;
  requestedLender: FinanceCompany;
  requestType: string;
  channel: string;
  affiliate: string;
  resubCount: number | null;
  assignedTo: string | null;
  assignedOn: string | null;
  status: RequestStatus;
}

export interface UWActionPayload {
  quoteUnderwritingRequestId: number;
  applicationId: number;
  handledBy: string;
  handledOn: string;
  /** FinanceCompany code (TINYINT) of the lender the decision applies to. */
  actionedLender: number;
  decision: ActionDecision;
  notes: string | null;
}
export interface UWActionUpdatePayload {
  quoteUnderwritingActionId: number;
  quoteUnderwritingRequestId: number;
  handledBy: string;
  handledOn: string;
  actionedLender: number;
  decision: ActionDecision;
  notes: string | null;
}

export interface UnderwritingQueueClaim {
  underwritingQueueClaimId: number | null;
  applicationId: number;
  agentId: number | null;
  claimedBy: string | null;
  claimedOn: string | null;
}

// API may return camelCase or PascalCase depending on serialiser configuration.
interface RawTeamPerformanceItem {
  agentName: string;
  channel?: string;
  agentType?: string;
  totalHandled: number;
  approved: number;
  declined: number;
  alternative: number;
  moreInfo?: number;
}

// ─── Avatar helpers (deterministic colour from initials) ──────────────────────

const AVATAR_COLORS = [
  "bg-slate-700",
  "bg-purple-600",
  "bg-rose-600",
  "bg-teal-700",
  "bg-blue-600",
  "bg-amber-600",
  "bg-emerald-700",
  "bg-indigo-600",
];

function initialsFor(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function avatarColorFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ─── Map raw recent-activity → UnderwritingActivity ──────────────────────────

/** Map an API date range (YYYY-MM-DD...) onto a mock period bucket. */
function mockPeriodKey(periodStart?: string, periodEnd?: string): "Day" | "Week" | "Month" {
  if (!periodStart || !periodEnd) return "Day";
  const start = new Date(periodStart.slice(0, 10));
  const end = new Date(periodEnd.slice(0, 10));
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000);
  if (days <= 0) return "Day";
  if (days <= 7) return "Week";
  return "Month";
}

// Dashboard reads

const getDashboardStats = async (
  periodStart: string,
  periodEnd: string,
  signal?: AbortSignal
): Promise<UWDashboardStatsResponse> => {
  if (isOfflineMode()) return mockDashboardStatsPeriod[mockPeriodKey(periodStart, periodEnd)];
  const { data } = await apiClient.get<UWDashboardStatsResponse>(
    "/quote-underwriting/dashboard/stats",
    { params: { periodStart, periodEnd }, signal }
  );
  return data;
};

const getRequestsByType = async (
  periodStart: string,
  periodEnd: string,
  unique?: boolean,
  signal?: AbortSignal
): Promise<RequestTypeRow[]> => {
  if (isOfflineMode()) return mockRequestsByTypePeriod[mockPeriodKey(periodStart, periodEnd)];
  const { data } = await apiClient.get<RequestTypeRow[]>(
    "/quote-underwriting/dashboard/requests-by-type",
    { params: { periodStart, periodEnd, ...(unique ? { unique: true } : {}) }, signal }
  );
  return data;
};

const getTeamPerformance = async (
  periodStart: string,
  periodEnd: string,
  channel?: string,
  requestType?: string,
  signal?: AbortSignal
): Promise<AgentPerformanceRow[]> => {
  if (isOfflineMode()) {
    return mockTeamPerformancePeriod[mockPeriodKey(periodStart, periodEnd)]
      .filter((item) => !channel || item.channel === channel)
      .map((item) => ({
        ...item,
        initials: initialsFor(item.agentName),
        avatarColor: avatarColorFor(item.agentName),
      }));
  }
  const { data } = await apiClient.get<RawTeamPerformanceItem[]>(
    "/quote-underwriting/dashboard/team-performance",
    {
      params: {
        periodStart,
        periodEnd,
        ...(channel ? { channel } : {}),
        ...(requestType ? { requestType } : {}),
      },
      signal,
    }
  );
  return data.map((item) => ({
    ...item,
    initials: initialsFor(item.agentName),
    avatarColor: avatarColorFor(item.agentName),
    channel: (item.channel ?? "Consumer Direct") as import("@/models/underwriting").TeamChannel,
    agentType: item.agentType as "LO" | "Standard" | undefined,
  }));
};

export interface RecentActivityParams {
  /** Inclusive lower bound (e.g. "2026-06-01T00:00:00"). */
  periodStart?: string;
  /** Inclusive upper bound (e.g. "2026-06-30T23:59:59"). */
  periodEnd?: string;
  /** When true, include actioned requests (Approved/Declined/Alternative/MoreInfo/Cancelled). */
  includeActioned?: boolean;
  /** Safety cap on the number of rows returned. */
  topN?: number;
  requestType?: string;
  channel?: string;
}

const getRecentActivity = async (
  params: RecentActivityParams = {},
  signal?: AbortSignal
): Promise<UnderwritingActivity[]> => {
  const { periodStart, periodEnd, includeActioned, topN = 2000, requestType, channel } = params;
  if (isOfflineMode()) return mockUnderwritingDashboard.recentActivity;
  const { data } = await apiClient.get<UnderwritingActivity[]>(
    "/quote-underwriting/dashboard/recent-activity",
    {
      params: {
        ...(periodStart ? { periodStart } : {}),
        ...(periodEnd ? { periodEnd } : {}),
        ...(includeActioned ? { includeActioned: true } : {}),
        topN,
        ...(requestType ? { requestType } : {}),
        ...(channel ? { channel } : {}),
      },
      signal,
    }
  );
  return data;
};

// ─── Underwriting Queue reads ─────────────────────────────────────────────────

const getQueueDashboardStats = async (
  periodStart: string,
  periodEnd: string,
  signal?: AbortSignal
): Promise<UWQueueDashboardStats> => {
  if (isOfflineMode()) {
    return mockQueueDashboardStatsPeriod[mockPeriodKey(periodStart, periodEnd)];
  }
  const { data } = await apiClient.get<UWQueueDashboardStats>(
    "/quote-underwriting/queue/dashboard/stats",
    { params: { periodStart, periodEnd }, signal }
  );
  return data;
};

const getQueueTeamPerformance = async (
  periodStart: string,
  periodEnd: string,
  signal?: AbortSignal
): Promise<UWQueueAgentRow[]> => {
  if (isOfflineMode()) {
    return mockQueueTeamPerformancePeriod[mockPeriodKey(periodStart, periodEnd)];
  }
  const { data } = await apiClient.get<UWQueueAgentRow[]>(
    "/quote-underwriting/queue/dashboard/team-performance",
    { params: { periodStart, periodEnd }, signal }
  );
  return data;
};

const getQueueApplications = async (
  params: RecentActivityParams = {},
  signal?: AbortSignal
): Promise<UnderwritingActivity[]> => {
  const { periodStart, periodEnd, topN = 2000, requestType, channel } = params;
  if (isOfflineMode()) return mockQueueApplications;
  const { data } = await apiClient.get<UnderwritingActivity[]>(
    "/quote-underwriting/queue/applications",
    {
      params: {
        ...(periodStart ? { periodStart } : {}),
        ...(periodEnd ? { periodEnd } : {}),
        topN,
        ...(requestType ? { requestType } : {}),
        ...(channel ? { channel } : {}),
      },
      signal,
    }
  );
  return data;
};

// ─── Write operations ─────────────────────────────────────────────────────────

/**
 * Records what the underwriter did to a queued application. The API closes the
 * queue claim in the same transaction, so this must not be paired with a
 * separate claim delete.
 */
const createQueueAction = async (
  payload: UWQueueActionPayload
): Promise<{ underwritingQueueActionId: number }> => {
  if (isOfflineMode()) return { underwritingQueueActionId: Date.now() };
  const { data } = await apiClient.post<{ underwritingQueueActionId: number }>(
    "/quote-underwriting/queue/actions",
    payload
  );
  return data;
};

const createRequest = async (
  payload: UWRequestPayload
): Promise<{ quoteUnderwritingRequestId: number }> => {
  const { data } = await apiClient.post<{ quoteUnderwritingRequestId: number }>(
    "/quote-underwriting/requests",
    payload
  );
  return data;
};

const updateRequest = async (id: number, payload: UWRequestPayload): Promise<void> => {
  await apiClient.put(`/quote-underwriting/requests/${id}`, payload);
};

const createAction = async (
  requestId: number,
  payload: UWActionPayload
): Promise<{ quoteUnderwritingActionId: number }> => {
  if (isOfflineMode()) return { quoteUnderwritingActionId: Date.now() };
  const { data } = await apiClient.post<{ quoteUnderwritingActionId: number }>(
    `/quote-underwriting/requests/${requestId}/actions`,
    payload
  );
  return data;
};

const updateAction = async (
  requestId: number,
  actionId: number,
  payload: UWActionUpdatePayload
): Promise<void> => {
  if (isOfflineMode()) return;
  await apiClient.put(
    `/quote-underwriting/requests/${requestId}/actions/${actionId}`,
    payload
  );
};

// Notes

const getNotes = async (
  applicationId: number,
  signal?: AbortSignal
): Promise<NoteModel[]> => {
  if (isOfflineMode()) return [];
  const { data } = await apiClient.get<NoteModel[]>(
    `/quote-underwriting/notes/${applicationId}`,
    { signal }
  );
  return Array.isArray(data) ? data : [];
};

const addNote = async (payload: AddNoteRequestModel): Promise<void> => {
  await apiClient.post(`/quote-underwriting/notes/${payload.applicationId}`, payload);
};

// Additional lender questions

const getAdditionalLenderQuestions = async (
  applicationId: number,
  signal?: AbortSignal
): Promise<AdditionalLenderQuestions | null> => {
  if (isOfflineMode()) return null;
  const { data } = await apiClient.get<Record<string, unknown> | null>(
    `/additional-lender-questions/${applicationId}`,
    { signal }
  );
  return parseAdditionalLenderQuestions(data);
};

const saveAdditionalLenderQuestions = async (
  applicationId: number,
  questions: AdditionalLenderQuestions
): Promise<void> => {
  if (isOfflineMode()) return;
  await apiClient.post(
    `/additional-lender-questions/${applicationId}`,
    serializeAdditionalLenderQuestions(questions)
  );
};

// History / Transcripts

// Raw shapes returned by the Customer Journey gateway endpoints.
interface RawAgentAssistSummary {
  loanApplicationId: number;
  recordingId: string;
  agentEmail: string;
  conversationDate: string;
  phoneNumber: string;
  summary: string;
}

interface RawChatBotSummary {
  loanApplicationId: number;
  conversationId: string;
  conversationDate: string;
  summary: string;
  createdDate: string;
  transcript: TranscriptSegment[] | null;
}

interface RawCallTranscript {
  id: string;
  segments: CallSegment[];
}

/** Application timeline — mirrors the `application/{id}/notes` workscreen endpoint. */
const getHistory = async (
  applicationId: number,
  signal?: AbortSignal
): Promise<NoteModel[]> => {
  if (isOfflineMode()) return [];
  const { data } = await apiClient.get<NoteListResponse>(
    `/application/${applicationId}/notes`,
    { signal }
  );
  return Array.isArray(data?.noteList) ? data.noteList : [];
};

/** Turn a name-or-email into a display name (e.g. "jane.doe@x.com" → "Jane Doe"). */
function agentDisplayName(agentEmail: string): string {
  const local = agentEmail.split("@")[0] ?? agentEmail;
  return local
    .split(/[._]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Transcripts tab data — combines Agent Assist call summaries and chatbot summaries
 * into a single, date-descending list. Call transcripts are fetched lazily on expand.
 */
const getTranscripts = async (
  applicationId: number,
  signal?: AbortSignal
): Promise<TranscriptEntry[]> => {
  if (isOfflineMode()) return [];
  const [calls, chatbots] = await Promise.all([
    apiClient
      .get<RawAgentAssistSummary[]>(
        `/agentassist/callsummary?loanApplicationId=${applicationId}`,
        { signal }
      )
      .then((r) => (Array.isArray(r.data) ? r.data : [])),
    apiClient
      .get<RawChatBotSummary[]>(
        `/agentassist/chatbotsummary?loanApplicationId=${applicationId}`,
        { signal }
      )
      .then((r) => (Array.isArray(r.data) ? r.data : [])),
  ]);

  const callEntries: TranscriptEntry[] = calls.map((c, i) => ({
    id: c.recordingId || `call-${i}`,
    channel: "Call",
    title: agentDisplayName(c.agentEmail),
    date: new Date(c.conversationDate).toISOString(),
    summary: c.summary || null,
    recordingId: c.recordingId || null,
    segments: null,
  }));
  const chatbotEntries: TranscriptEntry[] = chatbots.map((c, i) => ({
    id: c.conversationId || `chatbot-${i}`,
    channel: "ChatBot",
    title: "ChatBot",
    date: new Date(c.conversationDate).toISOString(),
    summary: c.summary || null,
    recordingId: null,
    segments: c.transcript ?? [],
  }));

  return [...callEntries, ...chatbotEntries].sort((a, b) =>
    a.date < b.date ? 1 : -1
  );
};

/** Core application details for the Overview panel (customer, status, dates, allocation). */
const getBaseApplicationDetails = async (
  applicationId: number,
  signal?: AbortSignal
): Promise<BaseApplicationDetails> => {
  if (isOfflineMode()) return mockBaseApplicationDetails;
  const { data } = await apiClient.get<BaseApplicationDetails>(
    `/quote-underwriting/base-application-details/${applicationId}`,
    { signal }
  );
  return data;
};

/** Prepare-application data for the queue quote view: base application (customer),
 * lender proposals, and the latest saved finance figures. Replaces the quote snapshot. */
const getPrepareApplication = async (
  applicationId: number,
  signal?: AbortSignal
): Promise<PrepareApplicationResponse> => {
  if (isOfflineMode()) return mockPrepareApplication;
  const { data } = await apiClient.get<PrepareApplicationResponse>(
    `/quote-underwriting/queue/prepare-application/${applicationId}`,
    { signal }
  );
  return data;
};

/** Live lender proposals for an application from the gateway. */
const getAvailableProposals = async (
  applicationId: number,
  signal?: AbortSignal
): Promise<PrepareApplicationProposal[]> => {
  if (isOfflineMode()) return [];
  const { data } = await apiClient.get<PrepareApplicationProposal[]>(
    `/application/${applicationId}/available-proposals/v3/expand-prime`,
    { signal }
  );
  return Array.isArray(data) ? data : [];
};

/** Lazily fetch a call transcript for an expanded Transcripts row. */
const getCallTranscript = async (
  recordingId: string,
  signal?: AbortSignal
): Promise<TranscriptSegment[]> => {
  if (isOfflineMode()) return [];
  const { data } = await apiClient.get<RawCallTranscript>(
    `/agentassist/calltranscript/${recordingId}`,
    { signal }
  );
  const segments = Array.isArray(data?.segments) ? data.segments : [];
  return segments.map((segment) => ({
    speaker: segment.speaker,
    content: segment.text,
  }));
};

// Messages (SMS / WhatsApp)

/** Normalises a UK number in local `0...` format to E.164 `+44...`; leaves other formats as-is. */
function toE164UkNumber(phoneNumber: string): string {
  return phoneNumber.startsWith("0") ? `+44${phoneNumber.slice(1)}` : phoneNumber;
}

/**
 * Customer phone number for the messages/history lookup, taken from the customer
 * object on the application's latest quote snapshot (`mobileNumber`). The pricing
 * library's `Customer` type doesn't declare this field, so it's read via a narrow
 * cast. Falls back to the Agent Assist call-summary endpoint (each call summary
 * carries the `phoneNumber` dialled for that application) if no snapshot exists.
 */
async function getCustomerPhoneNumber(
  applicationId: number,
  signal?: AbortSignal
): Promise<string | null> {
  const snapshots = await quoteSnapshotService
    .listByApp(applicationId, signal)
    .catch((err) => {
      // Don't swallow cancellations (e.g. a stale request aborted on applicationId change) —
      // rethrowing lets callers recognise it as a cancel instead of a genuine "no number" result.
      if (signal?.aborted) throw err;
      return [];
    });
  const mobileNumber = (
    snapshots[0]?.snapshot.customer as { mobileNumber?: string } | undefined
  )?.mobileNumber;
  if (mobileNumber) return toE164UkNumber(mobileNumber);

  const calls = await apiClient
    .get<RawAgentAssistSummary[]>(
      `/agentassist/callsummary?loanApplicationId=${applicationId}`,
      { signal }
    )
    .then((r) => (Array.isArray(r.data) ? r.data : []))
    .catch((err) => {
      if (signal?.aborted) throw err;
      return [];
    });
  const phoneNumber = calls.find((c) => c.phoneNumber)?.phoneNumber;
  return phoneNumber ? toE164UkNumber(phoneNumber) : null;
}

function toMessageEntries(
  messages: TwilioMessage[] | null | undefined,
  channel: MessageChannel
): MessageEntry[] {
  if (!Array.isArray(messages)) return [];
  return messages.map((m) => ({
    id: m.sid,
    channel,
    body: m.body,
    direction: m.direction?.startsWith("outbound") ? "outbound" : "inbound",
    from: m.from,
    to: m.to,
    date: m.dateCreated,
    status: m.status ?? null,
    errorMessage: m.errorMessage ?? null,
    mediaUrls: Array.isArray(m.mediaUrls) ? m.mediaUrls.filter((url): url is string => typeof url === "string") : [],
  }));
}

const getMessages = async (
  applicationId: number,
  signal?: AbortSignal
): Promise<MessageEntry[]> => {
  if (isOfflineMode()) return [];
  const number = await getCustomerPhoneNumber(applicationId, signal);
  if (!number) throw new Error("No customer phone number available.");
  const { data: twilioHistoryResponse } = await apiClient.post<TwilioHistoryResponse>(
    "/twilio-flex-helpers/messages/history-by-portal",
    { applicationId, number },
    { signal, headers: { Authorization: "Bearer " + (getSsoToken() ?? "") } }
  );
  const events = twilioHistoryResponse?.messageData?.Events ?? [];
  const entries = events.flatMap((event) => [
    ...toMessageEntries(event.Entries?.SmsMessages, "SMS"),
    ...toMessageEntries(event.Entries?.WhatsappMessages, "WhatsApp"),
    ...toMessageEntries(event.Entries?.RcsMessages, "RCS"),
  ]);
  return entries.sort((a, b) => (a.date < b.date ? -1 : 1));
};

// Queue claims

const claimRequest = async (
  activity: UnderwritingActivity,
  assignedTo: string
): Promise<void> => {
  if (isOfflineMode()) return;
  if (!activity.quoteUnderwritingRequestId) throw new Error("No requestId on activity");
  await apiClient.patch(`/quote-underwriting/requests/${activity.quoteUnderwritingRequestId}/claim`, {
    claimedBy: assignedTo,
  });
};

const getQueueHistory = async (signal?: AbortSignal): Promise<UnderwritingQueueClaimHistory[]> => {
  if (isOfflineMode()) return mockQueueClaimHistory();
  const { data } = await apiClient.get<UnderwritingQueueClaimHistory[]>(
    "/quote-underwriting/queue/dashboard/history",
    { signal }
  );
  return data;
};

const getQueueClaimByApplicationId = async (
  applicationId: number,
  signal?: AbortSignal
): Promise<UnderwritingQueueClaim | null> => {
  if (isOfflineMode()) {
    return {
      underwritingQueueClaimId: null,
      applicationId,
      agentId: null,
      claimedBy: null,
      claimedOn: null,
    };
  }
  const { data } = await apiClient.get<UnderwritingQueueClaim | null>(
    `/quote-underwriting/queue/claims/application/${applicationId}`,
    { signal }
  );
  return data;
};

/** Batch claim-status check for every application visible on the Underwriting Queue page. */
const getQueueClaimsByApplicationIds = async (
  applicationIds: number[],
  signal?: AbortSignal
): Promise<UnderwritingQueueClaim[]> => {
  if (isOfflineMode() || applicationIds.length === 0) return [];
  const { data } = await apiClient.post<UnderwritingQueueClaim[]>(
    "/quote-underwriting/queue/claims/batch",
    applicationIds,
    { signal }
  );
  return Array.isArray(data) ? data : [];
};

/** Claims an application, or takes over an existing claim once it's stale (>= 20 min old). */
const claimQueue = async (
  applicationId: number,
  agentId: number,
  claimedBy: string
): Promise<UnderwritingQueueClaim> => {
  if (isOfflineMode()) {
    return {
      underwritingQueueClaimId: Date.now(),
      applicationId,
      agentId,
      claimedBy,
      claimedOn: new Date().toISOString(),
    };
  }
  const { data } = await apiClient.post<UnderwritingQueueClaim>(
    `/quote-underwriting/queue/claims/${applicationId}`,
    { agentId, claimedBy }
  );
  return data;
};

const deleteQueueClaim = async (applicationId: number, releaseReason?: "Unclaimed" | "Abandoned"): Promise<void> => {
  if (isOfflineMode()) return;
  await apiClient.delete(`/quote-underwriting/queue/claims/${applicationId}`, {
    params: releaseReason ? { releaseReason } : undefined,
  });
};

/** Releases a request back to the queue so anyone can claim it. */
const unclaimRequest = async (
  activity: UnderwritingActivity,
  assignedTo: string
): Promise<void> => {
  if (isOfflineMode()) return;
  if (!activity.quoteUnderwritingRequestId) throw new Error("No requestId on activity");
  await apiClient.delete(
    `/quote-underwriting/requests/${activity.quoteUnderwritingRequestId}/claim?claimedBy=${encodeURIComponent(assignedTo)}`
  );
};

export const underwritingService = {
  getDashboardStats,
  getRequestsByType,
  getTeamPerformance,
  getRecentActivity,
  getQueueDashboardStats,
  getQueueTeamPerformance,
  getQueueApplications,
  createQueueAction,
  createRequest,
  updateRequest,
  createAction,
  updateAction,
  getNotes,
  addNote,
  getAdditionalLenderQuestions,
  saveAdditionalLenderQuestions,
  getHistory,
  getTranscripts,
  getCallTranscript,
  getMessages,
  getBaseApplicationDetails,
  getPrepareApplication,
  getAvailableProposals,
  claimRequest,
  getQueueHistory,
  getQueueClaimByApplicationId,
  getQueueClaimsByApplicationIds,
  claimQueue,
  deleteQueueClaim,
  unclaimRequest,
};
